export interface FilterLayoutItem {
    id: string;
    hidden: boolean;
}

/** Presentation only: never writes to DashboardFilter.hidden or selection state. */
export class FilterLayoutPreferences {
    order: string[] = [];
    visibility: Record<string, boolean> = {};
    expanded: Record<string, boolean> = {};

    constructor(private storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
        private key?: string) {
        try {
            const saved = JSON.parse(storage?.getItem(key) || 'null');
            if (saved?.version !== 1) return;
            if (Array.isArray(saved.order)) {
                this.order = [...new Set(saved.order.filter(id => typeof id === 'string'))] as string[];
            }
            for (const field of ['visibility', 'expanded'] as const) {
                if (saved[field] && typeof saved[field] === 'object' && !Array.isArray(saved[field])) {
                    this[field] = Object.fromEntries(Object.entries(saved[field]).filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'));
                }
            }
        } catch { /* Corrupt or unavailable storage falls back to the default layout. */ }
    }

    ordered<T extends FilterLayoutItem>(items: T[]): T[] {
        const rank = new Map(this.order.map((id, index) => [id, index]));
        return [...items].sort((a, b) => (rank.get(a.id) ?? this.order.length) - (rank.get(b.id) ?? this.order.length));
    }

    isVisible(item: FilterLayoutItem, singleHotel = false): boolean {
        return this.visibility[item.id] ?? (!item.hidden && !singleHotel);
    }

    isExpanded(item: FilterLayoutItem): boolean {
        return this.expanded[item.id] ?? item.id === 'hotel';
    }

    setVisible(id: string, visible: boolean): boolean {
        this.visibility[id] = visible;
        return this.save();
    }

    toggleExpanded(item: FilterLayoutItem): boolean {
        this.expanded[item.id] = !this.isExpanded(item);
        return this.save();
    }

    move(items: FilterLayoutItem[], from: number, to: number): boolean {
        if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return this.save();
        const ordered = this.ordered(items).map(item => item.id);
        const [id] = ordered.splice(from, 1);
        ordered.splice(to, 0, id);
        // Retain preferences for filters absent from this dashboard.
        this.order = [...ordered, ...this.order.filter(savedId => !ordered.includes(savedId))];
        return this.save();
    }

    reset(): boolean {
        this.order = [];
        this.visibility = {};
        this.expanded = {};
        return this.save();
    }

    save(): boolean {
        if (!this.storage || !this.key) return false;
        try {
            this.storage.setItem(this.key, JSON.stringify({ version: 1, order: this.order,
                visibility: this.visibility, expanded: this.expanded }));
            return true;
        } catch { return false; }
    }
}
